import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 pr-10 text-white text-sm box-border focus:outline-none focus:border-accent';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('resetPassword.missingToken'));
      return;
    }
    const strongEnough = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword);
    if (!strongEnough) {
      setError(t('resetPassword.passwordRequirementsError'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/v1/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('resetPassword.invalidToken'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-panel min-h-screen flex items-center justify-center font-sans px-4">
      <div className="bg-surface border border-edge rounded-2xl p-8 sm:p-10 w-full max-w-[360px]">
        <h1 className="text-accent text-xl font-extrabold mb-1">{t('common.appName')}</h1>
        <p className="text-muted text-[13px] mb-7">{t('resetPassword.title')}</p>

        {success ? (
          <>
            <p className="text-accent text-[13px] mb-6">{t('resetPassword.success')}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-accent border-none rounded-lg py-3 text-panel text-sm font-bold cursor-pointer"
            >
              {t('forgotPassword.backToLogin')}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="text-[#888] text-xs block mb-1.5">{t('resetPassword.newPassword')}</label>
            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                tabIndex={-1}
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>
            <p className="text-muted text-[11px] mb-4 -mt-3">{t('resetPassword.passwordHint')}</p>

            <label className="text-[#888] text-xs block mb-1.5">{t('resetPassword.confirmPassword')}</label>
            <div className="relative mb-5">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                tabIndex={-1}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            </div>

            {error && <p className="text-red-400 text-[13px] mb-4">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-accent border-none rounded-lg py-3 text-panel text-sm font-bold cursor-pointer ${loading ? 'opacity-70' : ''}`}
            >
              {loading ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;